import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../store';
import { setCredentials } from '../store/authSlice';
import api from '../services/api';

interface Project {
  _id: string;
  name: string;
  builderKey: string;
  location?: { latitude: number; longitude: number };
}

const ProfileScreen: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { user, token } = useSelector((state: RootState) => state.auth);

  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [collaborationVisible, setCollaborationVisible] = useState(false);
  const [savingVisibility, setSavingVisibility] = useState(false);

  // Password change state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  // Load user data and projects
  useEffect(() => {
    loadUserData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadUserData = async () => {
    try {
      setLoading(true);

      // Get current user data
      const userRes = await api.get('/auth/me');
      const updatedUser = userRes.data.user;
      dispatch(setCredentials({
        user: {
          id: updatedUser.id,
          name: updatedUser.name,
          email: updatedUser.email,
          role: updatedUser.role as 'admin' | 'designer' | 'client',
          projects: updatedUser.projects || [],
        },
        token: token || '',
      }));
      if (updatedUser.role === 'designer') {
        setCollaborationVisible(updatedUser.collaborationVisible || false);
      }

      // For client users, load all their assigned projects
      if (updatedUser.role === 'client' && updatedUser.projects?.length) {
        // Load all client projects
        const projectPromises = updatedUser.projects.map((projectId: string) =>
          api.get(`/projects/${projectId}`)
        );
        const projectResponses = await Promise.all(projectPromises);
        setProjects(projectResponses.map((res: any) => res.data));
      }
      // For designers, load all their projects
      else if (updatedUser.role === 'designer') {
        const projectsRes = await api.get('/projects');
        setProjects(projectsRes.data || []);
      }
      // Admins have no project access by default
      else {
        setProjects([]);
      }
    } catch (error) {
      console.error('Error loading user data:', error);
      alert('Failed to load profile data. Please refresh the page.');
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async () => {
    try {
      if (!currentPassword || !newPassword || !confirmPassword) {
        alert('Please fill in all password fields');
        return;
      }

      if (newPassword !== confirmPassword) {
        alert('New password and confirmation do not match');
        return;
      }

      if (newPassword.length < 6) {
        alert('New password must be at least 6 characters long');
        return;
      }

      setSaving(true);

      await api.patch('/users/change-password', {
        currentPassword,
        newPassword,
      });

      alert('Password updated successfully!');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error: any) {
      console.error('Password change error:', error);
      const message = error.response?.data?.error || 'Failed to update password';
      alert(message);
    } finally {
      setSaving(false);
    }
  };

  const handleToggleCollaborationVisibility = async () => {
    const next = !collaborationVisible;
    setSavingVisibility(true);
    try {
      await api.patch('/users/me/collaboration-visibility', { visible: next });
      setCollaborationVisible(next);
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to update collaboration visibility');
    } finally {
      setSavingVisibility(false);
    }
  };

  const handleProjectClick = (project: Project) => {
    navigate(`/project/${project._id}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-blue-50 flex items-center justify-center">
        <div className="text-lg">Loading profile...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-blue-50">
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold">My Profile</h1>
          {/* Role-based navigation */}
          {user?.role === 'client' ? (
            <button
              onClick={() => navigate(`/map/${projects[0]?._id || ''}`)}
              className="bg-gray-600 text-white px-4 py-2 rounded-md hover:bg-gray-700 transition-colors"
            >
              Back to Map
            </button>
          ) : (
            <button
              onClick={() => navigate('/')}
              className="bg-gray-600 text-white px-4 py-2 rounded-md hover:bg-gray-700 transition-colors"
            >
              Back to Dashboard
            </button>
          )}
        </div>

        {/* User Information */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h2 className="text-xl font-semibold mb-4">Profile Information</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
              <div className="bg-gray-50 px-3 py-2 rounded border">{user?.name || 'N/A'}</div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
              <div className="bg-gray-50 px-3 py-2 rounded border">{user?.email || 'N/A'}</div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
              <div className="bg-gray-50 px-3 py-2 rounded border capitalize">{user?.role || 'N/A'}</div>
            </div>
          </div>

          {/* Collaboration visibility toggle — designers only */}
          {user?.role === 'designer' && (
            <div className="mt-6 pt-5 border-t border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-900">Visible for collaboration</p>
                  <p className="text-xs text-gray-500 mt-0.5">
                    Allow other designers to find you when inviting collaborators to their projects.
                  </p>
                </div>
                <button
                  onClick={handleToggleCollaborationVisibility}
                  disabled={savingVisibility}
                  className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:opacity-50 ${
                    collaborationVisible ? 'bg-indigo-600' : 'bg-gray-200'
                  }`}
                  role="switch"
                  aria-checked={collaborationVisible}
                >
                  <span
                    className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                      collaborationVisible ? 'translate-x-5' : 'translate-x-0'
                    }`}
                  />
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Projects Section */}
        {(user?.role === 'designer' || user?.role === 'client') && (
          <div className="bg-white rounded-lg shadow-md p-6 mb-6">
            <h2 className="text-xl font-semibold mb-4">
              {user?.role === 'client' ? 'Assigned Projects' : 'My Projects'}
            </h2>

            {projects.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                {user?.role === 'designer'
                  ? 'No projects created yet'
                  : 'No projects assigned yet'}
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {projects.map((project) => (
                  <div
                    key={project._id}
                    className="border rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer"
                    onClick={() => handleProjectClick(project)}
                  >
                    <h3 className="font-semibold text-blue-600 mb-2">{project.name}</h3>
                    {project.location && (
                      <div className="text-sm text-gray-600 mb-2">
                        Location: {project.location.latitude.toFixed(4)}, {project.location.longitude.toFixed(4)}
                      </div>
                    )}
                    <button className="text-blue-600 hover:text-blue-800 text-sm font-medium">
                      View Details →
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Password Change Section */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold mb-4">Change Password</h2>

          <div className="space-y-4 max-w-md">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Current Password
              </label>
              <input
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                placeholder="Enter current password"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                New Password
              </label>
              <input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                placeholder="Enter new password"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Confirm New Password
              </label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                placeholder="Confirm new password"
              />
            </div>

            <div className="pt-2">
              <button
                onClick={handleChangePassword}
                disabled={saving || !currentPassword || !newPassword || !confirmPassword}
                className="w-full bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {saving ? 'Updating...' : 'Update Password'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfileScreen;
